import { Toaster, toast as hotToast } from 'react-hot-toast'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

/**
 * Brand-styled toast notifications
 *
 * Following brand animation principles:
 * - Slide down from top, stay 4-6 seconds, fade out
 * - Never: Shake, bounce repeatedly, or flash
 *
 * Error message tone (per brand guidelines):
 * - Calm, helpful, actionable
 * - "Something didn't work on our end" not "Error 500"
 */

interface ToastProviderProps {
  position?: 'top-center' | 'top-right' | 'bottom-center' | 'bottom-right'
}

export function ToastProvider({ position = 'top-center' }: ToastProviderProps) {
  return (
    <Toaster
      position={position}
      gutter={12}
      toastOptions={{
        duration: 5000,
        className: '',
        style: {
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
        },
      }}
    />
  )
}

interface ToastContentProps {
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  onDismiss?: () => void
}

function ToastContent({ type, title, message, onDismiss }: ToastContentProps) {
  const icons = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InformationCircleIcon,
  }

  const colors = {
    success: {
      bg: 'bg-white',
      border: 'border-success-200',
      icon: 'text-success-500',
    },
    error: {
      bg: 'bg-white',
      border: 'border-error-200',
      icon: 'text-error-500',
    },
    warning: {
      bg: 'bg-white',
      border: 'border-warning-200',
      icon: 'text-warning-500',
    },
    info: {
      bg: 'bg-white',
      border: 'border-info-200',
      icon: 'text-info-500',
    },
  }

  const Icon = icons[type]
  const colorConfig = colors[type]

  return (
    <div
      className={clsx(
        'flex items-start gap-3 p-4 rounded-card shadow-card border',
        'animate-slide-in min-w-[320px] max-w-md',
        colorConfig.bg,
        colorConfig.border
      )}
    >
      <Icon className={clsx('w-5 h-5 flex-shrink-0 mt-0.5', colorConfig.icon)} />
      <div className="flex-1 min-w-0">
        <p className="font-outfit font-medium text-body-sm text-stone-800">{title}</p>
        {message && (
          <p className="font-inter text-body-sm text-stone-500 mt-0.5">{message}</p>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-stone-400 hover:text-stone-600 transition-colors"
        >
          <XCircleIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  )
}

/**
 * Toast helper functions with brand-appropriate messaging
 */
export const toast = {
  success: (title: string, message?: string) => {
    return hotToast.custom((t) => (
      <ToastContent
        type="success"
        title={title}
        message={message}
        onDismiss={() => hotToast.dismiss(t.id)}
      />
    ))
  },

  error: (title: string, message?: string) => {
    // Apply brand-appropriate error messaging
    const brandTitle = title.includes('Error')
      ? "Something didn't work"
      : title
    const brandMessage = message || "We're looking into it. Try again in a moment."

    return hotToast.custom((t) => (
      <ToastContent
        type="error"
        title={brandTitle}
        message={brandMessage}
        onDismiss={() => hotToast.dismiss(t.id)}
      />
    ))
  },

  warning: (title: string, message?: string) => {
    return hotToast.custom((t) => (
      <ToastContent
        type="warning"
        title={title}
        message={message}
        onDismiss={() => hotToast.dismiss(t.id)}
      />
    ))
  },

  info: (title: string, message?: string) => {
    return hotToast.custom((t) => (
      <ToastContent
        type="info"
        title={title}
        message={message}
        onDismiss={() => hotToast.dismiss(t.id)}
      />
    ))
  },

  /**
   * Celebration toast for positive moments
   * Per brand: "Make the positive moments feel rewarding"
   */
  celebrate: (title: string, message?: string) => {
    return hotToast.custom((t) => (
      <div
        className={clsx(
          'flex items-start gap-3 p-4 rounded-card shadow-card',
          'animate-scale-in min-w-[320px] max-w-md',
          'bg-gradient-to-r from-mint-100 to-teal-50 border border-mint-200'
        )}
      >
        <span className="text-2xl">🎉</span>
        <div className="flex-1 min-w-0">
          <p className="font-outfit font-medium text-body-sm text-teal-700">{title}</p>
          {message && (
            <p className="font-inter text-body-sm text-teal-600 mt-0.5">{message}</p>
          )}
        </div>
        <button
          onClick={() => hotToast.dismiss(t.id)}
          className="text-teal-400 hover:text-teal-600 transition-colors"
        >
          <XCircleIcon className="w-5 h-5" />
        </button>
      </div>
    ))
  },

  dismiss: hotToast.dismiss,
  dismissAll: () => hotToast.dismiss(),
}

export { Toast }

function Toast() {
  return null // Export for type purposes
}
